import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { fetchUserProfile, getUserDetails, getRecordedDays, getRecordedDayFiles, getGogglesCrashes, getCrashDetails, generateDataset, desymbolicate, getLogDays, getLogDayFiles, getLogContent } from '../services/usersService';
import { addRecentUser } from '../utils/userProfiles';
import { getSkiGroupDetails } from '../services/skiGroupsService';
import { getProfilePictureUrl } from '../services/storageService';
import { fetchSessions } from '../services/analyticsService';
import SessionDetailView from '../components/SessionDetailView';
import { useError } from '../contexts/ErrorContext';
import { getHardwareModel } from '../utils/hardware';
import { reorderAppLogContent, RAW_APP_LOG_WARNING } from '../utils/mergeAppLog';
import { formatChronologicalGogglesLog } from '../utils/gogglesLog';
import {
  ANALYTICS_TIMEZONE,
  getBrowserTimeZone,
  getTimeZoneList,
  wallTimeToInstant,
  instantToWallTime,
  formatInTimeZone,
  timeZoneLabel,
} from '../utils/timezone';
import './UserDetailPage.css';

/**
 * Format a date for display
 * @param {Date|string|null} date - Date to format
 * @returns {string} Formatted date string
 */
const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleString();
};

/**
 * Format coordinates for display
 * @param {Object|null} coordinate - Coordinate object with latitude and longitude
 * @returns {string} Formatted coordinate string
 */
const formatCoordinate = (coordinate) => {
  if (!coordinate) return 'N/A';
  const { latitude, longitude } = coordinate;
  if (latitude === null || latitude === void 0 || longitude === null || longitude === void 0) {
    return 'N/A';
  }
  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
};

/**
 * Format software version from softwareInfo
 * @param {Object|null|undefined} softwareInfo - Software info object
 * @returns {string|null} Formatted version string or null (format: major.minor.patch.build)
 */
const formatSoftwareVersion = (softwareInfo) => {
  if (!softwareInfo) return null;
  const major = softwareInfo.majorVersion || 0;
  const minor = softwareInfo.minorVersion || 0;
  const patch = softwareInfo.patchVersion || 0;
  const build = softwareInfo.buildNumVersion || 0;
  return `v${major}.${minor}.${patch}.${build}`;
};

/**
 * Get Firebase Console URL for a skier document
 * @param {string} uid - User UID
 * @returns {string} Firebase Console URL
 */
const getFirebaseSkierUrl = (uid) => {
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'rekkie-app';
  return `https://console.firebase.google.com/project/${projectId}/firestore/databases/-default-/data/~2Fskiers~2F${uid}`;
};

/**
 * Get Firebase Console URL for a ski group document
 * @param {string} groupId - Ski group ID
 * @returns {string} Firebase Console URL
 */
const getFirebaseSkiGroupUrl = (groupId) => {
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'rekkie-app';
  return `https://console.firebase.google.com/project/${projectId}/firestore/databases/-default-/data/~2Fskigroups~2F${groupId}`;
};

/**
 * Format day string from MM_DD_YYYY to readable format
 * @param {string} dayStr - Day string in MM_DD_YYYY format
 * @returns {string} Formatted date string
 */
const formatDayString = (dayStr) => {
  const parts = dayStr.split('_');
  if (parts.length === 3) {
    const [month, day, year] = parts;
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    }
  }
  return dayStr; // Return original if parsing fails
};

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'recorded-days', label: 'Recorded Days' },
  { id: 'goggles-crashes', label: 'Goggles Crashes' },
  { id: 'logs', label: 'Logs' },
  { id: 'analytics', label: 'Analytics' },
];

const VALID_TABS = TABS.map(t => t.id);

// Tab ids that were renamed; old bookmarks and shared links still resolve to the new tab.
const LEGACY_TAB_IDS = { 'app-logs': 'logs' };

// The two kinds of files under logs/{uid}/{day}/. Clicking a file downloads it raw; the
// `postProcess` button downloads it after client-side processing. `process` returns the new
// text, or null when the file has nothing it can process.
const LOG_TYPES = [
  {
    id: 'app',
    label: 'App Logs',
    countKey: 'appFileCount',
    emptyText: 'No app logs found',
    postProcess: {
      buttonLabel: 'Download (sorted)',
      statusLabel: 'Reordering…',
      fileSuffix: '.sorted.txt',
      process: reorderAppLogContent,
      unprocessableError: 'No timestamped entries found in this log file',
    },
  },
  {
    id: 'goggles',
    label: 'Goggles Logs',
    countKey: 'gogglesFileCount',
    emptyText: 'No goggles logs found',
    postProcess: {
      buttonLabel: 'Download (chronological, PT)',
      statusLabel: 'Reordering…',
      fileSuffix: '.chronological.txt',
      process: formatChronologicalGogglesLog,
      unprocessableError: 'No "# session=… epoch=…" marker found in this log file, so records cannot be grouped or timed',
    },
  },
];

const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { addError } = useError();
  const [searchParams, setSearchParams] = useSearchParams();

  // Drive activeTab from URL
  const rawTabParam = searchParams.get('tab');
  const tabParam = LEGACY_TAB_IDS[rawTabParam] ?? rawTabParam;
  const activeTab = VALID_TABS.includes(tabParam) ? tabParam : 'overview';

  const setActiveTab = (tab) => {
    setSearchParams(tab === 'overview' ? {} : { tab });
  };

  const [user, setUser] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  // On phones the tab list is a horizontal strip; keep the active tab scrolled into view
  // (a deep link to ?tab=analytics would otherwise open with its tab off-screen).
  const tabsNavRef = React.useRef(null);
  React.useEffect(() => {
    const nav = tabsNavRef.current;
    const active = nav?.querySelector('.user-detail-tab.active');
    if (!nav || !active || nav.scrollWidth <= nav.clientWidth) return;
    const navRect = nav.getBoundingClientRect();
    const tabRect = active.getBoundingClientRect();
    nav.scrollLeft += tabRect.left - navRect.left - (navRect.width - tabRect.width) / 2;
  }, [activeTab, loading]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [recordedDays, setRecordedDays] = useState([]);
  const [recordedDaysLoading, setRecordedDaysLoading] = useState(false);
  const [gogglesCrashes, setGogglesCrashes] = useState([]);
  const [gogglesCrashesLoading, setGogglesCrashesLoading] = useState(false);
  const [selectedCrash, setSelectedCrash] = useState(null); // { day, crash }
  const [crashDetails, setCrashDetails] = useState(null);
  const [crashDetailsLoading, setCrashDetailsLoading] = useState(false);
  const [desymbolicateResult, setDesymbolicateResult] = useState(null);
  const [desymbolicateLoading, setDesymbolicateLoading] = useState(false);
  const [expandedRecordedDay, setExpandedRecordedDay] = useState(null);
  const [recordedDayFiles, setRecordedDayFiles] = useState([]);
  const [recordedDayFilesLoading, setRecordedDayFilesLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState(new Set()); // Set of selected filenames
  const [logDays, setLogDays] = useState([]); // [{day, appFileCount, gogglesFileCount}]
  const [logsLoading, setLogsLoading] = useState(false);
  const [logType, setLogType] = useState('app'); // 'app' | 'goggles'
  const [expandedLogDay, setExpandedLogDay] = useState(null);
  const [logDayFiles, setLogDayFiles] = useState([]);
  const [logDayFilesLoading, setLogDayFilesLoading] = useState(false);
  const [downloadingLogFile, setDownloadingLogFile] = useState(null); // filename currently downloading
  const [processingLogFile, setProcessingLogFile] = useState(null); // filename currently being post-processed
  const [showDatasetModal, setShowDatasetModal] = useState(false);
  const [datasetName, setDatasetName] = useState('');
  const [datasetGenerating, setDatasetGenerating] = useState(false);
  const [datasetResult, setDatasetResult] = useState(null); // { success: bool, message: string }
  const [skiGroupDetails, setSkiGroupDetails] = useState(null);
  const [skiGroupLoading, setSkiGroupLoading] = useState(false);

  // Analytics state
  const [analyticsPreset, setAnalyticsPreset] = useState('1d'); // '1d', '7d', '30d', 'custom'
  
  // The zone the Start/End fields are measured in. Defaults to the viewer's own
  // zone; a `tz` in the URL overrides it, which is how the Goggles Connectivity
  // drill-down pins the window to the GA4 analytics zone regardless of who clicks.
  const [analyticsTimeZone, setAnalyticsTimeZone] = useState(
    () => searchParams.get('tz') || getBrowserTimeZone()
  );
  const timeZoneOptions = React.useMemo(() => getTimeZoneList(), []);

  const [analyticsStartTime, setAnalyticsStartTime] = useState(() => {
    const tz = searchParams.get('tz') || getBrowserTimeZone();
    const startParam = searchParams.get('startDate');
    if (startParam) return instantToWallTime(new Date(startParam), tz);
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return instantToWallTime(d, tz);
  });
  const [analyticsEndTime, setAnalyticsEndTime] = useState(() => {
    const tz = searchParams.get('tz') || getBrowserTimeZone();
    const endParam = searchParams.get('endDate');
    if (endParam) return instantToWallTime(new Date(endParam), tz);
    return instantToWallTime(new Date(), tz);
  });
  const [sessionsData, setSessionsData] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null); // Session object for detail view
  // Range actually queried by the last loadSessions call, kept in the URL so
  // analytics links (list view and session detail) are shareable
  const [loadedRange, setLoadedRange] = useState(() => {
    const startParam = searchParams.get('startDate');
    const endParam = searchParams.get('endDate');
    return startParam && endParam ? { start: startParam, end: endParam } : null;
  });

  // Load the user's display profile by uid. A uid with no Firestore doc or Auth
  // record still opens, labelled by its uid, since storage and analytics data can
  // outlive the account.
  const loadUser = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setProfilePictureUrl(null);
      const profile = (await fetchUserProfile(userId, addError)) || {
        uid: userId, name: null, email: null, profilePicturePath: null, initials: null
      };
      setUser(profile);
      if (profile.name || profile.email) addRecentUser(profile);
      if (profile.profilePicturePath) {
        const url = await getProfilePictureUrl(profile.profilePicturePath);
        setProfilePictureUrl(url);
      }
    } catch (err) {
      console.error('Failed to load user:', err);
      const errorMsg = err.message || 'Failed to load user';
      setError(errorMsg);
      addError(errorMsg, err.stack, 'User Detail Page');
    } finally {
      setLoading(false);
    }
  }, [userId, addError]);

  // Fetch user details
  const loadDetails = useCallback(async () => {
    if (!userId) return;
    try {
      setDetailsLoading(true);
      const data = await getUserDetails(userId, addError);
      if (data) {
        setDetails(data);
      }
    } catch (err) {
      console.error('Failed to load user details:', err);
      addError(err.message || 'Failed to load user details', err.stack, 'User Detail Page');
    } finally {
      setDetailsLoading(false);
    }
  }, [userId, addError]);

  // Fetch ski group details when details are loaded
  const loadSkiGroupDetails = useCallback(async (skiGroupId) => {
    if (!skiGroupId) {
      setSkiGroupDetails(null);
      return;
    }
    try {
      setSkiGroupLoading(true);
      const data = await getSkiGroupDetails(skiGroupId, addError);
      setSkiGroupDetails(data);
    } catch (err) {
      console.error('Failed to load ski group details:', err);
      // Don't show error notification - just set null
      setSkiGroupDetails(null);
    } finally {
      setSkiGroupLoading(false);
    }
  }, [addError]);

  // Fetch recorded days
  const loadRecordedDays = useCallback(async () => {
    if (!userId) return;
    try {
      setRecordedDaysLoading(true);
      const days = await getRecordedDays(userId, addError);
      setRecordedDays(days);
    } catch (err) {
      console.error('Failed to load recorded days:', err);
      addError(err.message || 'Failed to load recorded days', err.stack, 'User Detail Page');
    } finally {
      setRecordedDaysLoading(false);
    }
  }, [userId, addError]);

  // Fetch log days (app and goggles counts come back together)
  const loadLogs = useCallback(async () => {
    if (!userId) return;
    try {
      setLogsLoading(true);
      const days = await getLogDays(userId, addError);
      setLogDays(days);
    } catch (err) {
      console.error('Failed to load logs:', err);
      addError(err.message || 'Failed to load logs', err.stack, 'User Detail Page');
    } finally {
      setLogsLoading(false);
    }
  }, [userId, addError]);

  // Fetch goggles crashes
  const loadGogglesCrashes = useCallback(async () => {
    if (!userId) return;
    try {
      setGogglesCrashesLoading(true);
      const crashes = await getGogglesCrashes(userId, addError);
      setGogglesCrashes(crashes);
    } catch (err) {
      console.error('Failed to load goggles crashes:', err);
      addError(err.message || 'Failed to load goggles crashes', err.stack, 'User Detail Page');
    } finally {
      setGogglesCrashesLoading(false);
    }
  }, [userId, addError]);

  // Fetch crash details when a crash is selected
  const loadCrashDetails = useCallback(async (day, crash) => {
    if (!userId || !crash.filename) return;
    try {
      setCrashDetailsLoading(true);
      setCrashDetails(null);
      const crashData = await getCrashDetails(userId, day, crash.filename, addError);
      setCrashDetails(crashData);
    } catch (err) {
      console.error('Failed to load crash details:', err);
      addError(err.message || 'Failed to load crash details', err.stack, 'User Detail Page');
    } finally {
      setCrashDetailsLoading(false);
    }
  }, [userId, addError]);

  const handleCrashClick = (day, crash) => {
    setSelectedCrash({ day, crash });
    loadCrashDetails(day, crash);
    setSearchParams({ tab: 'goggles-crashes', day, crashFile: crash.filename });
  };

  const handleCloseCrashDetails = () => {
    setSelectedCrash(null);
    setCrashDetails(null);
    setDesymbolicateResult(null);
    setSearchParams({ tab: 'goggles-crashes' });
  };

  // Handle desymbolicate button click
  const handleDesymbolicate = async () => {
    if (!selectedCrash || desymbolicateLoading) return;
    
    setDesymbolicateLoading(true);
    setDesymbolicateResult(null);
    
    try {
      const result = await desymbolicate(
        userId,
        selectedCrash.day,
        selectedCrash.crash.filename,
        addError
      );
      setDesymbolicateResult(result);
    } catch (err) {
      setDesymbolicateResult({ error: err.message });
    } finally {
      setDesymbolicateLoading(false);
    }
  };

  // Handle clicking on a recorded day to expand/collapse
  const handleRecordedDayClick = async (day) => {
    if (expandedRecordedDay === day) {
      // Collapse if already expanded
      setExpandedRecordedDay(null);
      setRecordedDayFiles([]);
      setSelectedFiles(new Set());
    } else {
      // Expand and load files
      setExpandedRecordedDay(day);
      setSelectedFiles(new Set());
      setRecordedDayFilesLoading(true);
      try {
        const files = await getRecordedDayFiles(userId, day, addError);
        setRecordedDayFiles(files);
      } catch (err) {
        console.error('Failed to load recorded day files:', err);
        addError(err.message || 'Failed to load files', err.stack, 'User Detail Page');
        setRecordedDayFiles([]);
      } finally {
        setRecordedDayFilesLoading(false);
      }
    }
  };

  // Handle clicking on a log day to expand/collapse
  const handleLogDayClick = async (day) => {
    if (expandedLogDay === day) {
      // Collapse if already expanded
      setExpandedLogDay(null);
      setLogDayFiles([]);
    } else {
      // Expand and load files of the selected log type
      setExpandedLogDay(day);
      setLogDayFilesLoading(true);
      try {
        const files = await getLogDayFiles(userId, day, logType, addError);
        setLogDayFiles(files);
      } catch (err) {
        console.error('Failed to load log files:', err);
        addError(err.message || 'Failed to load files', err.stack, 'User Detail Page');
        setLogDayFiles([]);
      } finally {
        setLogDayFilesLoading(false);
      }
    }
  };

  // Switch between app and goggles logs; the open day's file list belongs to the old type
  const handleLogTypeChange = (type) => {
    if (type === logType) return;
    setLogType(type);
    setExpandedLogDay(null);
    setLogDayFiles([]);
  };

  // Handle clicking on a log file to download it
  const handleLogFileClick = async (day, filename) => {
    // The warning is about app logs (app-side and system-level blocks in one file)
    if (logType === 'app') window.alert(RAW_APP_LOG_WARNING);
    setDownloadingLogFile(filename);
    try {
      const content = await getLogContent(userId, day, filename, addError);
      if (content === null) return;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download log file:', err);
      addError(err.message || 'Failed to download log file', err.stack, 'User Detail Page');
    } finally {
      setDownloadingLogFile(null);
    }
  };

  // Handle clicking to download a log file after the selected log type's post-processing
  const handleLogFilePostProcessedDownload = async (day, filename) => {
    const { process, fileSuffix, unprocessableError } = LOG_TYPES.find((t) => t.id === logType).postProcess;
    setProcessingLogFile(filename);
    try {
      const content = await getLogContent(userId, day, filename, addError);
      if (content === null) return;
      const processed = process(content);
      if (processed === null) {
        addError(unprocessableError, null, 'User Detail Page');
        return;
      }
      const blob = new Blob([processed], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename.replace(/\.txt$/, fileSuffix);
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download post-processed log file:', err);
      addError(err.message || 'Failed to download post-processed log file', err.stack, 'User Detail Page');
    } finally {
      setProcessingLogFile(null);
    }
  };

  // Toggle selection of a single file
  const handleFileSelect = (filename) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(filename)) {
        newSet.delete(filename);
      } else {
        newSet.add(filename);
      }
      return newSet;
    });
  };

  // Toggle selection of all files
  const handleSelectAllFiles = () => {
    if (selectedFiles.size === recordedDayFiles.length) {
      // All selected, deselect all
      setSelectedFiles(new Set());
    } else {
      // Select all
      setSelectedFiles(new Set(recordedDayFiles));
    }
  };

  // Handle generate dataset button click - opens modal
  const handleGenerateDataset = () => {
    setShowDatasetModal(true);
    setDatasetName('');
    setDatasetResult(null);
  };

  // Handle dataset modal close
  const handleCloseDatasetModal = () => {
    if (!datasetGenerating) {
      setShowDatasetModal(false);
      setDatasetName('');
      setDatasetResult(null);
    }
  };

  // Handle dataset generation submission
  const handleSubmitDataset = async () => {
    if (!datasetName.trim() || datasetGenerating) return;
    
    setDatasetGenerating(true);
    setDatasetResult(null);
    
    try {
      const result = await generateDataset(
        userId,
        expandedRecordedDay,
        Array.from(selectedFiles),
        datasetName.trim(),
        addError
      );
      
      setDatasetResult({
        success: true,
        message: result.message,
        stats: result.stats
      });
      
      // Clear selection on success
      setSelectedFiles(new Set());
    } catch (err) {
      setDatasetResult({
        success: false,
        message: err.message || 'Failed to generate dataset'
      });
    } finally {
      setDatasetGenerating(false);
    }
  };

  // Handle buddy click - open that user's page
  const handleBuddyClick = (e, buddy) => {
    if (buddy.uid === userId) {
      return;
    }
    const path = `/users/${buddy.uid}`;
    if (e.metaKey || e.ctrlKey) {
      window.open(path, '_blank');
    } else {
      navigate(path);
    }
  };

  useEffect(() => {
    loadUser();
    loadDetails();
    loadRecordedDays();
    loadGogglesCrashes();
    loadLogs();
  }, [loadUser, loadDetails, loadRecordedDays, loadGogglesCrashes, loadLogs]);

  // Restore crash selection from URL params after crashes load
  useEffect(() => {
    const dayParam = searchParams.get('day');
    const crashFileParam = searchParams.get('crashFile');
    if (dayParam && crashFileParam && gogglesCrashes.length > 0 && !selectedCrash) {
      const dayEntry = gogglesCrashes.find(d => d.day === dayParam);
      if (dayEntry && dayEntry.crashes) {
        const crash = dayEntry.crashes.find(c => c.filename === crashFileParam);
        if (crash) {
          setSelectedCrash({ day: dayParam, crash });
          loadCrashDetails(dayParam, crash);
        }
      }
    }
  }, [gogglesCrashes, searchParams, selectedCrash, loadCrashDetails]);

  // Restore session selection from URL params after sessions load (initial load only)
  const sessionRestoredRef = React.useRef(false);
  useEffect(() => {
    const sessionParam = searchParams.get('session');
    if (sessionParam && sessionsData?.sessions && !sessionRestoredRef.current) {
      const session = sessionsData.sessions.find(s => s.sessionId === sessionParam);
      if (session) {
        sessionRestoredRef.current = true;
        setSelectedSession(session);
      }
    }
  }, [sessionsData, searchParams]);

  // Load ski group details when user details are loaded and contain a ski group
  useEffect(() => {
    if (details?.skiGroup) {
      loadSkiGroupDetails(details.skiGroup);
    } else {
      setSkiGroupDetails(null);
    }
  }, [details?.skiGroup, loadSkiGroupDetails]);

  const handleBack = () => {
    navigate('/users');
  };

  const renderOverviewTab = () => {
    if (detailsLoading) {
      return <div className="user-detail-loading">Loading details...</div>;
    }

    if (!details) {
      return <div className="user-detail-empty">No details available</div>;
    }

    return (
      <div className="user-detail-overview">
        {/* Header with updated timestamp */}
        <div className="overview-header">
          <span className="overview-updated">Updated: {formatDate(details.updatedAt)}</span>
        </div>

        {/* Telemetry */}
        <div className="user-detail-section">
          <h4 className="section-title">
            Telemetry
            {details.telemetry?.timestamp && (
              <span className="section-title-timestamp">
                {' | Last Updated: '}{formatDate(details.telemetry.timestamp)}
              </span>
            )}
          </h4>
          {details.telemetry ? (
            <div className="telemetry-grid">
              <div className="telemetry-item">
                <span className="telemetry-label">Coordinate</span>
                <span className="telemetry-value">
                  {formatCoordinate(details.telemetry.coordinate)}
                </span>
              </div>
              {details.telemetry.altitude !== null && details.telemetry.altitude !== void 0 && (
                <div className="telemetry-item">
                  <span className="telemetry-label">Altitude</span>
                  <span className="telemetry-value">
                    {details.telemetry.altitude.toFixed(1)} m
                  </span>
                </div>
              )}
              {details.telemetry.speed !== null && details.telemetry.speed !== void 0 && (
                <div className="telemetry-item">
                  <span className="telemetry-label">Speed</span>
                  <span className="telemetry-value">
                    {details.telemetry.speed.toFixed(1)} m/s
                  </span>
                </div>
              )}
              {details.telemetry.heading !== null && details.telemetry.heading !== void 0 && (
                <div className="telemetry-item">
                  <span className="telemetry-label">Heading</span>
                  <span className="telemetry-value">
                    {details.telemetry.heading.toFixed(0)}°
                  </span>
                </div>
              )}
              {details.telemetry.horizAccuracy !== null && details.telemetry.horizAccuracy !== void 0 && (
                <div className="telemetry-item">
                  <span className="telemetry-label">Accuracy</span>
                  <span className="telemetry-value">
                    ±{details.telemetry.horizAccuracy.toFixed(1)} m
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="section-empty">No telemetry data</div>
          )}
        </div>

        {/* Ski Group */}
        <div className="user-detail-section">
          <h4 className="section-title">Ski Group</h4>
          {details.skiGroup ? (
            <div className="ski-group-details">
              {skiGroupLoading ? (
                <div className="section-loading">Loading ski group...</div>
              ) : skiGroupDetails ? (
                <>
                  {/* Ski Group Name */}
                  <div className="ski-group-item">
                    <span className="ski-group-label">Name</span>
                    <span className="ski-group-value">{skiGroupDetails.name || 'Unnamed'}</span>
                  </div>
                  
                  {/* Updated At */}
                  {skiGroupDetails.updatedAt && (
                    <div className="ski-group-item">
                      <span className="ski-group-label">Last Updated</span>
                      <span className="ski-group-value">{formatDate(skiGroupDetails.updatedAt)}</span>
                    </div>
                  )}
                  
                  {/* Buddies */}
                  <div className="ski-group-item">
                    <span className="ski-group-label">Buddies</span>
                    <span className="ski-group-value">{skiGroupDetails.buddies?.length || 0} members</span>
                  </div>
                  
                  {skiGroupDetails.buddies && skiGroupDetails.buddies.length > 0 && (
                    <div className="ski-group-buddies">
                      {skiGroupDetails.buddies.map((buddy, index) => (
                        <div 
                          key={index} 
                          className={`buddy-item ${buddy.uid !== userId ? 'clickable' : ''}`}
                          onClick={(e) => handleBuddyClick(e, buddy)}
                        >
                          <div className="buddy-info">
                            <span className="buddy-name">{buddy.name || 'Unknown'}</span>
                            <span className="buddy-email">{buddy.email || 'No email'}</span>
                          </div>
                          <div className="buddy-uid-container">
                            <a
                              href={getFirebaseSkierUrl(buddy.uid)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="buddy-uid-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {buddy.uid}
                            </a>
                            {buddy.uid === userId && <span className="buddy-you">(you)</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Firebase Link */}
                  <div className="ski-group-item">
                    <span className="ski-group-label">ID</span>
                    <a
                      href={getFirebaseSkiGroupUrl(details.skiGroup)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ski-group-link"
                    >
                      {details.skiGroup}
                    </a>
                  </div>
                </>
              ) : (
                <div className="section-value">
                  <a
                    href={getFirebaseSkiGroupUrl(details.skiGroup)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ski-group-link"
                  >
                    {details.skiGroup}
                  </a>
                </div>
              )}
            </div>
          ) : (
            <div className="section-empty">No ski group</div>
          )}
        </div>

        {/* Goggles Meta - Only render if gogglesMeta exists */}
        {details.gogglesMeta && (
          <div className="user-detail-section">
            <h4 className="section-title">Goggles</h4>
            <div className="goggles-grid">
              {/* Hardware Model from hwBuildInfoProtoField */}
              {getHardwareModel(details.gogglesMeta.hwBuildInfoProtoField) && (
                <div className="goggles-item">
                  <span className="goggles-label">Hardware Model</span>
                  <span className="goggles-value">
                    {getHardwareModel(details.gogglesMeta.hwBuildInfoProtoField)}
                  </span>
                </div>
              )}
              {/* Software Version from softwareVersionInfo */}
              {formatSoftwareVersion(details.gogglesMeta.softwareVersionInfo) && (
                <div className="goggles-item">
                  <span className="goggles-label">Software</span>
                  <span className="goggles-value">
                    {formatSoftwareVersion(details.gogglesMeta.softwareVersionInfo)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Firmware Channel */}
        {details.gogglesFirmwareChannel && (
          <div className="user-detail-section">
            <h4 className="section-title">Firmware Channel</h4>
            <div className="section-value firmware-badge">
              {details.gogglesFirmwareChannel}
            </div>
          </div>
        )}

        {/* UID - tiny text at bottom */}
        <div className="user-uid-footer">{userId}</div>
      </div>
    );
  };

  const renderDatasetModal = () => {
    if (!showDatasetModal) return null;

    return (
      <div className="dataset-modal-overlay" onClick={handleCloseDatasetModal}>
        <div className="dataset-modal" onClick={(e) => e.stopPropagation()}>
          <div className="dataset-modal-header">
            <h3 className="dataset-modal-title">Create Dataset</h3>
            {!datasetGenerating && !datasetResult?.success && (
              <button className="dataset-modal-close" onClick={handleCloseDatasetModal}>×</button>
            )}
          </div>
          
          <div className="dataset-modal-content">
            {datasetGenerating ? (
              <div className="dataset-generating">
                <div className="dataset-spinner"></div>
                <p>Generating dataset...</p>
                <p className="dataset-generating-info">
                  Merging {selectedFiles.size} files from {expandedRecordedDay}
                </p>
              </div>
            ) : datasetResult ? (
              <div className={`dataset-result ${datasetResult.success ? 'success' : 'error'}`}>
                <div className={`dataset-result-icon ${datasetResult.success ? 'success' : 'error'}`}>
                  {datasetResult.success ? '✓' : '✕'}
                </div>
                <p className="dataset-result-message">{datasetResult.message}</p>
                {datasetResult.success && datasetResult.stats && (
                  <div className="dataset-result-stats">
                    <span>{datasetResult.stats.filesProcessed} files processed</span>
                    <span>{datasetResult.stats.totalLocations} locations</span>
                  </div>
                )}
                <button 
                  className="dataset-modal-button primary"
                  onClick={handleCloseDatasetModal}
                >
                  {datasetResult.success ? 'Done' : 'Close'}
                </button>
              </div>
            ) : (
              <>
                <p className="dataset-modal-info">
                  Create a dataset from {selectedFiles.size} selected files
                </p>
                <label className="dataset-modal-label">Dataset Name</label>
                <input
                  type="text"
                  className="dataset-modal-input"
                  placeholder="Enter dataset name..."
                  value={datasetName}
                  onChange={(e) => setDatasetName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && datasetName.trim()) {
                      handleSubmitDataset();
                    }
                  }}
                  autoFocus
                />
                <div className="dataset-modal-actions">
                  <button 
                    className={`dataset-modal-button primary ${!datasetName.trim() ? 'disabled' : ''}`}
                    onClick={handleSubmitDataset}
                    disabled={!datasetName.trim()}
                  >
                    Create Dataset
                  </button>
                  <button 
                    className="dataset-modal-button secondary"
                    onClick={handleCloseDatasetModal}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderRecordedDaysTab = () => {
    if (recordedDaysLoading) {
      return <div className="user-detail-loading">Loading recorded days...</div>;
    }

    if (recordedDays.length === 0) {
      return <div className="user-detail-empty">No recorded days found</div>;
    }

    const allFilesSelected = recordedDayFiles.length > 0 && selectedFiles.size === recordedDayFiles.length;
    const someFilesSelected = selectedFiles.size > 0;

    return (
      <div className="recorded-days-container">
        {renderDatasetModal()}
        <div className="recorded-days-header">
          <h3 className="recorded-days-title">Recorded Days</h3>
          <div className="recorded-days-header-right">
            <span className="recorded-days-count">{recordedDays.length} days</span>
            <button 
              className={`generate-dataset-button ${someFilesSelected ? '' : 'disabled'}`}
              onClick={handleGenerateDataset}
              disabled={!someFilesSelected}
            >
              Generate Dataset {someFilesSelected && `(${selectedFiles.size})`}
            </button>
          </div>
        </div>
        <div className="recorded-days-list">
          {recordedDays.map((day) => {
            const isExpanded = expandedRecordedDay === day.day;
            return (
              <div key={day.day} className="recorded-day-card">
                <div 
                  className={`recorded-day-header ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => handleRecordedDayClick(day.day)}
                >
                  <div className="recorded-day-header-left">
                    <span className={`recorded-day-chevron ${isExpanded ? 'expanded' : ''}`}>▶</span>
                    <span className="recorded-day-date">{formatDayString(day.day)}</span>
                  </div>
                  <span className="recorded-day-files">{day.fileCount} files</span>
                </div>
                {isExpanded && (
                  <div className="recorded-day-files-list">
                    {recordedDayFilesLoading ? (
                      <div className="recorded-day-files-loading">Loading files...</div>
                    ) : recordedDayFiles.length === 0 ? (
                      <div className="recorded-day-files-empty">No files found</div>
                    ) : (
                      <>
                        <div 
                          className="recorded-day-select-all"
                          onClick={(e) => { e.stopPropagation(); handleSelectAllFiles(); }}
                        >
                          <input 
                            type="checkbox"
                            checked={allFilesSelected}
                            onChange={handleSelectAllFiles}
                            onClick={(e) => e.stopPropagation()}
                            className="file-checkbox"
                          />
                          <span className="select-all-label">
                            {allFilesSelected ? 'Deselect All' : 'Select All'}
                          </span>
                        </div>
                        {recordedDayFiles.map((filename, index) => (
                          <div 
                            key={index} 
                            className={`recorded-day-file-item ${selectedFiles.has(filename) ? 'selected' : ''}`}
                            onClick={(e) => { e.stopPropagation(); handleFileSelect(filename); }}
                          >
                            <input 
                              type="checkbox"
                              checked={selectedFiles.has(filename)}
                              onChange={() => handleFileSelect(filename)}
                              onClick={(e) => e.stopPropagation()}
                              className="file-checkbox"
                            />
                            <span className="file-name">{filename}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const formatLogFileSize = (bytes) => {
    if (typeof bytes !== 'number' || bytes <= 0) return null;
    const units = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(1))} ${units[i]}`;
  };

  const renderLogsTab = () => {
    if (logsLoading) {
      return <div className="user-detail-loading">Loading logs...</div>;
    }

    if (logDays.length === 0) {
      return <div className="user-detail-empty">No logs found</div>;
    }

    const activeLogType = LOG_TYPES.find((t) => t.id === logType);
    const daysOfType = logDays.filter((day) => day[activeLogType.countKey] > 0);

    return (
      <div className="recorded-days-container">
        <div className="recorded-days-header">
          <h3 className="recorded-days-title">Logs</h3>
          <div className="recorded-days-header-right">
            <div className="log-type-toggle" role="tablist" aria-label="Log type">
              {LOG_TYPES.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  role="tab"
                  aria-selected={logType === type.id}
                  className={`log-type-option ${logType === type.id ? 'active' : ''}`}
                  onClick={() => handleLogTypeChange(type.id)}
                >
                  {type.label}
                </button>
              ))}
            </div>
            <span className="recorded-days-count">{daysOfType.length} days</span>
          </div>
        </div>
        {daysOfType.length === 0 ? (
          <div className="user-detail-empty">{activeLogType.emptyText}</div>
        ) : (
          <div className="recorded-days-list">
            {daysOfType.map((day) => {
              const isExpanded = expandedLogDay === day.day;
              return (
                <div key={day.day} className="recorded-day-card">
                  <div
                    className={`recorded-day-header ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => handleLogDayClick(day.day)}
                  >
                    <div className="recorded-day-header-left">
                      <span className={`recorded-day-chevron ${isExpanded ? 'expanded' : ''}`}>▶</span>
                      <span className="recorded-day-date">{formatDayString(day.day)}</span>
                    </div>
                    <span className="recorded-day-files">{day[activeLogType.countKey]} files</span>
                  </div>
                  {isExpanded && (
                    <div className="recorded-day-files-list">
                      {logDayFilesLoading ? (
                        <div className="recorded-day-files-loading">Loading files...</div>
                      ) : logDayFiles.length === 0 ? (
                        <div className="recorded-day-files-empty">No files found</div>
                      ) : (
                        logDayFiles.map((file) => (
                          <div
                            key={file.name}
                            className="recorded-day-file-item"
                            onClick={(e) => { e.stopPropagation(); handleLogFileClick(day.day, file.name); }}
                          >
                            <span className="file-name">{file.name}</span>
                            {formatLogFileSize(file.size) && (
                              <span className="file-size">{formatLogFileSize(file.size)}</span>
                            )}
                            <button
                              type="button"
                              className="file-action-sorted"
                              disabled={processingLogFile === file.name}
                              onClick={(e) => { e.stopPropagation(); handleLogFilePostProcessedDownload(day.day, file.name); }}
                            >
                              {activeLogType.postProcess.buttonLabel}
                            </button>
                            {downloadingLogFile === file.name && (
                              <span className="file-download-status">Downloading…</span>
                            )}
                            {processingLogFile === file.name && (
                              <span className="file-download-status">{activeLogType.postProcess.statusLabel}</span>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const formatCrashTime = (isoTimestamp) => {
    if (!isoTimestamp) return 'Unknown time';
    const date = new Date(isoTimestamp);
    if (isNaN(date.getTime())) return 'Unknown time';
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const renderCrashDetailsPanel = () => {
    if (!selectedCrash) return null;

    return (
      <div className="crash-details-overlay" onClick={handleCloseCrashDetails}>
        <div className="crash-details-panel" onClick={(e) => e.stopPropagation()}>
          <div className="crash-details-header">
            <h3 className="crash-details-title">Crash Details</h3>
            <button className="crash-details-close" onClick={handleCloseCrashDetails}>×</button>
          </div>
          
          <div className="crash-details-meta">
            <span className="crash-details-date">{formatDayString(selectedCrash.day)}</span>
            <span className="crash-details-time">{formatCrashTime(selectedCrash.crash.timestamp)}</span>
            <span className="crash-details-version">{selectedCrash.crash.version}</span>
          </div>

          <div className="crash-details-content">
            {crashDetailsLoading && (
              <div className="crash-details-loading">Loading crash details...</div>
            )}
            
            {!crashDetailsLoading && !crashDetails && (
              <div className="crash-details-error">Failed to load crash details</div>
            )}
            
            {!crashDetailsLoading && crashDetails && (
              <>
                <div className="crash-detail-section">
                  <h4 className="crash-detail-label">Task Name</h4>
                  <div className="crash-detail-value crash-detail-mono">{crashDetails.nameOfTask}</div>
                </div>

                <div className="crash-detail-section">
                  <h4 className="crash-detail-label">Core Dump Version</h4>
                  <div className="crash-detail-value">{crashDetails.coreDumpVersion}</div>
                </div>

                {crashDetails.extraInfo && (
                  <div className="crash-detail-section">
                    <h4 className="crash-detail-label">Exception Info</h4>
                    <div className="crash-detail-grid">
                      <div className="crash-detail-item">
                        <span className="crash-detail-item-label">Cause</span>
                        <span className="crash-detail-item-value">{crashDetails.extraInfo.cause}</span>
                      </div>
                      {crashDetails.extraInfo.virtualAddress && (
                        <div className="crash-detail-item">
                          <span className="crash-detail-item-label">Virtual Address</span>
                          <span className="crash-detail-item-value crash-detail-mono">
                            {crashDetails.extraInfo.virtualAddress}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {crashDetails.backtrace && (
                  <div className="crash-detail-section">
                    <h4 className="crash-detail-label">
                      Backtrace
                      {crashDetails.backtrace.corrupted && (
                        <span className="crash-backtrace-corrupted">CORRUPTED</span>
                      )}
                    </h4>
                    <div className="crash-detail-value">
                      <span className="crash-backtrace-depth">Depth: {crashDetails.backtrace.depth}</span>
                    </div>
                    {crashDetails.backtrace.addresses && crashDetails.backtrace.addresses.length > 0 && (
                      <div className="crash-backtrace-addresses">
                        {crashDetails.backtrace.addresses.map((addr, index) => (
                          <div key={index} className="crash-backtrace-address">
                            <span className="crash-backtrace-index">{index}</span>
                            <span className="crash-backtrace-addr">{addr}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {crashDetails.appElfSha256 && (
                  <div className="crash-detail-section">
                    <h4 className="crash-detail-label">App ELF SHA256</h4>
                    <div className="crash-detail-value crash-detail-mono crash-detail-sha">
                      {crashDetails.appElfSha256.length > 40 
                        ? crashDetails.appElfSha256 
                        : crashDetails.appElfSha256.toUpperCase().match(/.{1,8}/g)?.join(' ') || crashDetails.appElfSha256}
                    </div>
                    <div className="crash-detail-sha-note">
                      First 20 bytes of firmware ELF SHA256 (use to match firmware version)
                    </div>
                  </div>
                )}

                {crashDetails.fullText && (
                  <div className="crash-detail-section">
                    <h4 className="crash-detail-label">Full Proto Message</h4>
                    <pre className="crash-full-text">{crashDetails.fullText}</pre>
                  </div>
                )}

                {/* Desymbolicate Section */}
                <div className="crash-detail-section desymbolicate-section">
                  <div className="desymbolicate-header">
                    <h4 className="crash-detail-label">Desymbolicate Backtrace</h4>
                    <button 
                      className={`desymbolicate-button ${desymbolicateLoading ? 'loading' : ''}`}
                      onClick={handleDesymbolicate}
                      disabled={desymbolicateLoading || !crashDetails.backtrace?.addresses?.length}
                    >
                      {desymbolicateLoading ? 'Desymbolicating...' : 'Desymbolicate'}
                    </button>
                  </div>
                  
                  {desymbolicateResult && (
                    <div className="desymbolicate-result">
                      {desymbolicateResult.error ? (
                        <div className="desymbolicate-error">{desymbolicateResult.error}</div>
                      ) : (
                        <>
                          <div className="desymbolicate-info">
                            <span className="desymbolicate-elf-path">ELF: {desymbolicateResult.elfPath}</span>
                          </div>
                          <div className="desymbolicate-list">
                            {desymbolicateResult.symbolicated?.map((item, index) => (
                              <div key={index} className="desymbolicate-item">
                                <span className="desymbolicate-address">{item.address}</span>
                                <span className="desymbolicate-symbol">{item.symbol}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderGogglesCrashesTab = () => {
    if (gogglesCrashesLoading) {
      return <div className="user-detail-loading">Loading goggles crashes...</div>;
    }

    if (gogglesCrashes.length === 0) {
      return <div className="user-detail-empty">No goggles crashes found</div>;
    }

    // Calculate total crashes across all days
    const totalCrashes = gogglesCrashes.reduce((sum, day) => sum + day.crashCount, 0);

    return (
      <div className="goggles-crashes-container">
        {renderCrashDetailsPanel()}
        <div className="goggles-crashes-header">
          <h3 className="goggles-crashes-title">Goggles Crashes</h3>
          <span className="goggles-crashes-count">{totalCrashes} crashes across {gogglesCrashes.length} days</span>
        </div>
        <div className="goggles-crashes-list">
          {gogglesCrashes.map((day) => (
            <div key={day.day} className="goggles-crash-day">
              <div className="goggles-crash-day-header">
                <span className="goggles-crash-date">{formatDayString(day.day)}</span>
                <span className="goggles-crash-day-count">{day.crashCount} crashes</span>
              </div>
              <div className="goggles-crash-summaries">
                {day.crashes && day.crashes.map((crash, index) => (
                  <div 
                    key={index} 
                    className="goggles-crash-summary clickable"
                    onClick={() => handleCrashClick(day.day, crash)}
                  >
                    <span className="crash-time">{formatCrashTime(crash.timestamp)}</span>
                    <span className="crash-version">{crash.version}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Load sessions list (lightweight - no event details)
  const loadSessions = useCallback(async (startTime, endTime) => {
    if (!userId) return;
    
    setSessionsLoading(true);
    setSessionsData(null);
    setSelectedSession(null);
    setLoadedRange({ start: startTime, end: endTime });
    
    try {
      const sessionsResult = await fetchSessions(userId, startTime, endTime, addError);
      setSessionsData(sessionsResult);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setSessionsLoading(false);
    }
  }, [userId, addError]);

  // Auto-fetch sessions when analytics tab is opened with date params from URL
  const analyticsAutoFetchedRef = React.useRef(false);
  useEffect(() => {
    if (activeTab === 'analytics' && !analyticsAutoFetchedRef.current) {
      const startParam = searchParams.get('startDate');
      const endParam = searchParams.get('endDate');
      if (startParam && endParam) {
        analyticsAutoFetchedRef.current = true;
        const start = new Date(startParam);
        const end = new Date(endParam);
        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          loadSessions(start.toISOString(), end.toISOString());
        }
      }
    }
  }, [activeTab, searchParams, loadSessions]);

  // Write the analytics filters (and optionally the selected session) to the URL
  // so the current view can be copy/pasted and reopened identically
  const setAnalyticsSearchParams = (start, end, sessionId = null) => {
    const params = { tab: 'analytics' };
    if (start && end) {
      params.startDate = start;
      params.endDate = end;
      params.tz = analyticsTimeZone;
    }
    if (sessionId) params.session = sessionId;
    setSearchParams(params);
  };

  // Handle preset change
  const handlePresetChange = (preset) => {
    setAnalyticsPreset(preset);
    
    const now = new Date();
    let start = new Date();
    
    switch (preset) {
      case '1d':
        start.setDate(now.getDate() - 1);
        break;
      case '7d':
        start.setDate(now.getDate() - 7);
        break;
      case '30d':
        start.setDate(now.getDate() - 30);
        break;
      case 'custom':
        // Don't change times for custom
        return;
      default:
        start.setDate(now.getDate() - 1);
    }
    
    // Show the window in the selected zone; the API always takes instants.
    setAnalyticsStartTime(instantToWallTime(start, analyticsTimeZone));
    setAnalyticsEndTime(instantToWallTime(now, analyticsTimeZone));

    setAnalyticsSearchParams(start.toISOString(), now.toISOString());
    loadSessions(start.toISOString(), now.toISOString());
  };

  // Handle Go button click
  const handleAnalyticsGo = () => {
    // The fields hold wall-clock text; the selected zone turns it into an instant.
    const start = wallTimeToInstant(analyticsStartTime, analyticsTimeZone);
    const end = wallTimeToInstant(analyticsEndTime, analyticsTimeZone);

    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
      addError('Invalid date range', null, 'Analytics');
      return;
    }
    
    if (start >= end) {
      addError('Start time must be before end time', null, 'Analytics');
      return;
    }
    
    setAnalyticsSearchParams(start.toISOString(), end.toISOString());
    loadSessions(start.toISOString(), end.toISOString());
  };

  // Handle session click - open detail view
  const handleSessionClick = (session) => {
    setSelectedSession(session);
    setAnalyticsSearchParams(loadedRange?.start, loadedRange?.end, session.sessionId);
  };

  // Close session detail view
  const handleCloseSessionDetail = () => {
    sessionRestoredRef.current = false;
    setSelectedSession(null);
    setAnalyticsSearchParams(loadedRange?.start, loadedRange?.end);
  };

  // Format duration for display
  const formatDuration = (ms) => {
    if (!ms) return 'Unknown';
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const renderAnalyticsTab = () => {
    // Show session detail view if a session is selected
    if (selectedSession) {
      return (
        <div className="analytics-container">
          <SessionDetailView
            session={selectedSession}
            userId={userId}
            onBack={handleCloseSessionDetail}
          />
        </div>
      );
    }

    // Otherwise show sessions list
    return (
      <div className="analytics-container">
        <div className="analytics-header">
          <h3 className="analytics-title">Analytics</h3>
        </div>
        
        {/* Filters */}
        <div className="analytics-filters">
          <div className="analytics-presets">
            {['1d', '7d', '30d', 'custom'].map((preset) => (
              <button
                key={preset}
                className={`analytics-preset-btn ${analyticsPreset === preset ? 'active' : ''}`}
                onClick={() => handlePresetChange(preset)}
              >
                {preset === 'custom' ? 'Custom' : `Last ${preset}`}
              </button>
            ))}
          </div>
          
          <div className="analytics-date-range">
            <div className="analytics-date-input">
              <label>Start</label>
              <input
                type="datetime-local"
                value={analyticsStartTime}
                onChange={(e) => {
                  setAnalyticsStartTime(e.target.value);
                  setAnalyticsPreset('custom');
                }}
              />
            </div>
            <div className="analytics-date-input">
              <label>End</label>
              <input
                type="datetime-local"
                value={analyticsEndTime}
                onChange={(e) => {
                  setAnalyticsEndTime(e.target.value);
                  setAnalyticsPreset('custom');
                }}
              />
            </div>
            <div className="analytics-date-input analytics-tz-input">
              <label>Timezone</label>
              <select
                value={analyticsTimeZone}
                onChange={(e) => {
                  // Keep the wall-clock text and reinterpret it in the new zone —
                  // "8/21 00:00" should mean midnight *there*, not the same instant.
                  setAnalyticsTimeZone(e.target.value);
                  setAnalyticsPreset('custom');
                }}
              >
                {timeZoneOptions.map((tz) => (
                  <option key={tz} value={tz}>{timeZoneLabel(tz)}</option>
                ))}
              </select>
            </div>
            <button 
              className="analytics-go-btn"
              onClick={handleAnalyticsGo}
              disabled={sessionsLoading}
            >
              {sessionsLoading ? 'Loading...' : 'Go'}
            </button>
          </div>

          <p className="analytics-tz-note">
            Times shown in {timeZoneLabel(analyticsTimeZone)}
            {analyticsTimeZone !== ANALYTICS_TIMEZONE && (
              <>
                {' · '}
                <button
                  type="button"
                  className="analytics-tz-switch"
                  onClick={() => {
                    setAnalyticsTimeZone(ANALYTICS_TIMEZONE);
                    setAnalyticsPreset('custom');
                  }}
                >
                  Switch to report timezone
                </button>
              </>
            )}
          </p>
        </div>

        {/* Results */}
        <div className="analytics-results">
          {sessionsLoading && (
            <div className="analytics-loading">
              <div className="analytics-spinner"></div>
              <p>Loading sessions...</p>
            </div>
          )}
          
          {!sessionsLoading && !sessionsData && (
            <div className="analytics-empty">
              <div className="analytics-empty-icon">📊</div>
              <p>Select a time range and click Go to load sessions</p>
            </div>
          )}

          {/* Message if BigQuery not configured */}
          {sessionsData?.message && (
            <div className="analytics-message">
              <span className="analytics-message-icon">ℹ️</span>
              {sessionsData.message}
            </div>
          )}
          
          {!sessionsLoading && sessionsData && (
            <>
              {/* Summary */}
              <div className="analytics-summary">
                <div className="analytics-summary-card">
                  <span className="analytics-summary-value">{sessionsData.totalSessions || 0}</span>
                  <span className="analytics-summary-label">Sessions</span>
                </div>
              </div>

              {/* Sessions List */}
              {sessionsData.sessions && sessionsData.sessions.length > 0 ? (
                <div className="analytics-section">
                  <h4 className="analytics-section-title">Sessions</h4>
                  <div className="analytics-sessions-list">
                    {sessionsData.sessions.map((session, index) => (
                      <div 
                        key={session.sessionId || index} 
                        className="analytics-session-row clickable"
                        onClick={() => handleSessionClick(session)}
                      >
                        <div className="analytics-session-info">
                          <span className="analytics-session-time">
                            {session.startTime
                              ? formatInTimeZone(session.startTime, analyticsTimeZone, {
                                  month: 'short', day: 'numeric', year: 'numeric',
                                  hour: 'numeric', minute: '2-digit', second: '2-digit',
                                  timeZoneName: 'short',
                                })
                              : 'Unknown'}
                            {' – '}
                            {session.endTime
                              ? formatInTimeZone(session.endTime, analyticsTimeZone, {
                                  hour: 'numeric', minute: '2-digit', second: '2-digit',
                                  timeZoneName: 'short',
                                })
                              : 'Unknown'}
                          </span>
                          <span className="analytics-session-meta">
                            {session.eventCount} events • {session.device?.os || 'Unknown OS'} • {session.appVersion || 'Unknown version'}
                            {session.errorCount > 0 && (
                              <span className="session-error-badge">{session.errorCount} error{session.errorCount !== 1 ? 's' : ''}</span>
                            )}
                            {session.crashCount > 0 && (
                              <span className="session-crash-badge">{session.crashCount} crash{session.crashCount !== 1 ? 'es' : ''}</span>
                            )}
                          </span>
                        </div>
                        <div className="analytics-session-right">
                          {session.durationMs && (
                            <span className="analytics-session-duration">
                              {formatDuration(session.durationMs)}
                            </span>
                          )}
                          <span className="analytics-session-chevron">▶</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="analytics-empty">
                  <div className="analytics-empty-icon">📭</div>
                  <p>No sessions found for this time range</p>
                </div>
              )}

            </>
          )}
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'recorded-days':
        return renderRecordedDaysTab();
      case 'goggles-crashes':
        return renderGogglesCrashesTab();
      case 'logs':
        return renderLogsTab();
      case 'analytics':
        return renderAnalyticsTab();
      default:
        return renderOverviewTab();
    }
  };

  if (loading) {
    return (
      <div className="user-detail-page">
        <div className="user-detail-loading-container">
          <div className="user-detail-loading">Loading user...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-detail-page">
        <div className="user-detail-error-container">
          <div className="user-detail-error">{error}</div>
          <button className="user-detail-back-button" onClick={handleBack}>
            ← Back to Users
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="user-detail-page">
      {/* Left Sidebar with Tabs */}
      <aside className="user-detail-sidebar">
        {/* User Header */}
        <div className="user-detail-user-header">
          <button className="user-detail-back-button" onClick={handleBack}>
            ← Back
          </button>
          <div className="user-detail-user-info">
            <div className="user-detail-avatar">
              {profilePictureUrl ? (
                <img 
                  src={profilePictureUrl} 
                  alt={user?.name || 'User'} 
                  className="user-detail-avatar-image"
                  onError={() => setProfilePictureUrl(null)}
                />
              ) : (
                <div className="user-detail-avatar-initials">
                  {user?.initials || '?'}
                </div>
              )}
            </div>
            <div className="user-detail-user-text">
              <a 
                href={getFirebaseSkierUrl(userId)} 
                target="_blank" 
                rel="noopener noreferrer"
                className="user-detail-name-link"
              >
                {user?.name || userId}
              </a>
              <span className="user-detail-email">{user?.email || ''}</span>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <nav className="user-detail-tabs" ref={tabsNavRef}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              className={`user-detail-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Content Area */}
      <main className="user-detail-content">
        {renderTabContent()}
      </main>
    </div>
  );
};

export default UserDetailPage;
